// src/eh/sync.js
// ─────────────────────────────────────────────────────────────────────────────
// Sync worker: copies locally-captured records up to Firestore when the network
// and the server rules allow. Sync is strictly one-way (device → server) and
// strictly optional — the local store stays the source of truth, and a failed
// push leaves the record exactly where it was, marked for retry.
//
// Writes use the record's local id as the Firestore document id, so re-running
// a sync is idempotent rather than duplicating rows.
// ─────────────────────────────────────────────────────────────────────────────

import { COLLECTIONS, ehStore } from "./store";

// Firestore collection names are namespaced so the environmental-health data
// never collides with the legacy `reports` collection the waste app writes to.
const REMOTE_NAME = {
  [COLLECTIONS.hazardReports]: "eh_hazard_reports",
  [COLLECTIONS.surveyResponses]: "eh_survey_responses",
  [COLLECTIONS.activityLogs]: "eh_activity_logs",
  [COLLECTIONS.riskZones]: "eh_risk_zones",
  [COLLECTIONS.messageDeliveries]: "eh_message_deliveries",
};

const SYNCABLE = Object.keys(REMOTE_NAME);

export const isOnline = () =>
  typeof navigator === "undefined" ? true : navigator.onLine !== false;

/** Subscribe to connectivity changes. Returns an unsubscribe function. */
export const watchConnectivity = (fn) => {
  const on = () => fn(true);
  const off = () => fn(false);
  window.addEventListener("online", on);
  window.addEventListener("offline", off);
  return () => {
    window.removeEventListener("online", on);
    window.removeEventListener("offline", off);
  };
};

/**
 * Upload a photo's base64 payload to Firebase Storage and return its URL.
 * Photos are the heaviest part of a record and the part most likely to fail on
 * a weak link, so they go first — if this throws, the whole record is retried
 * later rather than being written half-complete.
 */
const uploadPhoto = async (photo, path) => {
  const { ref, uploadString, getDownloadURL } = await import("firebase/storage");
  const { storage } = await import("../firebase");
  const r = ref(storage, path);
  await uploadString(r, photo.dataUrl, "data_url");
  return getDownloadURL(r);
};

const stripLocalOnly = (record) => {
  // Base64 payloads never go into Firestore documents — the 1 MiB document
  // limit makes that a guaranteed failure once a couple of photos are attached.
  const photos = (record.photos || []).map(({ dataUrl, thumbUrl, ...rest }) => rest);
  return { ...record, photos, syncedAt: new Date().toISOString() };
};

/**
 * Push every pending record. Returns a summary; never throws.
 * `onProgress({done, total})` is optional.
 */
export const syncAll = async ({ onProgress } = {}) => {
  const config = ehStore.getConfig();
  if (!config.syncEnabled) {
    return { ok: false, reason: "disabled", pushed: 0, failed: 0 };
  }
  if (!isOnline()) {
    return { ok: false, reason: "offline", pushed: 0, failed: 0 };
  }

  let firestore;
  try {
    firestore = await import("firebase/firestore");
  } catch (err) {
    console.error("Sync: the Firestore SDK could not be loaded.", err);
    return { ok: false, reason: "firestore_unavailable", pushed: 0, failed: 0, error: err };
  }
  const { doc, setDoc } = firestore;
  const { db } = await import("../firebase");

  const queue = [];
  SYNCABLE.forEach((c) => {
    ehStore.all(c).forEach((r) => {
      if (r.syncState !== "synced") queue.push({ collection: c, record: r });
    });
  });

  let pushed = 0;
  let failed = 0;
  let firstError = null;

  for (let i = 0; i < queue.length; i += 1) {
    const { collection: c, record } = queue[i];
    try {
      let photos = record.photos || [];
      if (photos.length) {
        photos = await Promise.all(
          photos.map(async (p, idx) => {
            if (p.url || !p.dataUrl) return p;
            const url = await uploadPhoto(p, `eh/${c}/${record.id}/${idx}.jpg`);
            return { ...p, url };
          })
        );
        // Keep the uploaded URLs locally so a later re-sync skips the upload.
        await ehStore.markSync(c, record.id, record.syncState, { photos });
      }

      const payload = stripLocalOnly({ ...record, photos });
      await setDoc(doc(db, REMOTE_NAME[c], record.id), payload, { merge: true });
      await ehStore.markSync(c, record.id, "synced", { syncError: null });
      pushed += 1;
    } catch (err) {
      console.error(`Sync: record ${record.id} in ${c} failed to upload.`, err);
      failed += 1;
      if (!firstError) firstError = err;
      await ehStore.markSync(c, record.id, "error", {
        syncError: String(err?.message || err).slice(0, 300),
      });
    }
    if (onProgress) onProgress({ done: i + 1, total: queue.length });
  }

  return {
    ok: failed === 0,
    reason: failed ? "partial" : "done",
    pushed,
    failed,
    error: firstError,
  };
};

/**
 * Try a sync whenever the device comes back online, if sync is enabled.
 * Returns an unsubscribe function.
 */
export const startAutoSync = () => {
  let running = false;
  const attempt = async () => {
    if (running) return;
    if (!ehStore.getConfig().syncEnabled) return;
    if (!isOnline()) return;
    const { pending } = ehStore.syncStats();
    if (!pending) return;
    running = true;
    try {
      const summary = await syncAll();
      // Background sync has no UI of its own — the banner only shows a count.
      // Without this, a run that failed every record looked identical to one
      // that had nothing to do, and the reason was gone.
      if (summary.failed) {
        console.error(
          `Auto-sync: ${summary.failed} of ${summary.failed + summary.pushed} record(s) failed to upload.`,
          summary.error
        );
      }
    } catch (err) {
      // syncAll is written not to throw; if it ever does, losing it here would
      // leave the queue stuck with no trace of why.
      console.error("Auto-sync crashed:", err);
    } finally {
      running = false;
    }
  };
  const stopWatching = watchConnectivity((online) => {
    if (online) attempt();
  });
  attempt();
  return stopWatching;
};
